import { Component } from "@angular/core";
import { ColDef } from 'ag-grid-community';

import { CaseContextRecordPanelBase } from "@app/case-context/components/case-context-record-panel-base.directive";
import { InmateOffenseRecord } from "@app/case-context/models/inmate-offense-record.model";
import { CaseContextRecordGridComponent } from '../case-context-record-grid/case-context-record-grid.component';
import { labelValueFormatter, recordCodeValueGetter, recordDateValueFormatter, recordLabelValueGetter } from '../case-context-record-grid/case-context-record-grid-columns';
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PHMS_FACILITY_TERMINOLOGY } from "@app/phms/phms-facility.terminology";

/**
 * Institutional-conduct panel: renders neutral institutional conduct and
 * notable custody-event records. Accepts rows whose recordCategoryCode is
 * "institutional_conduct" or "custody_event" so a single neutral DTO surface
 * can drive multiple panels on the case-context page.
 *
 * Reuses the input-driven record grid and on-demand detail flyout so the
 * panel is embeddable from any Angular page; copy is jurisdiction-neutral
 * and source-system-neutral to satisfy AC #3.
 */
@Component({
  selector: "phms-case-context-institutional-conduct-panel",
  standalone: true,
  imports: [CaseContextRecordGridComponent, TerminologyIdentityDirective],
  templateUrl: "./case-context-institutional-conduct-panel.component.html",
})
export class CaseContextInstitutionalConductPanelComponent extends CaseContextRecordPanelBase {
  readonly facilityUsage = PHMS_FACILITY_TERMINOLOGY.caseContextConduct;
  readonly facilityText = createTerminologyUsageSignals(PHMS_FACILITY_TERMINOLOGY.caseContextConduct);
  readonly columnDefs: ColDef<InmateOffenseRecord>[] = [
    {field: 'eventDate', headerName: 'Date', width: 100, minWidth: 95, sort: 'desc', valueFormatter: recordDateValueFormatter},
    {field: 'recordCode', headerName: 'Code', width: 130, minWidth: 110, valueGetter: recordCodeValueGetter},
    {field: 'recordLabel', headerName: 'Record', flex: 1, minWidth: 200, valueGetter: recordLabelValueGetter},
    {field: 'severityCode', headerName: 'Severity', width: 100, valueFormatter: labelValueFormatter},
    {field: 'dispositionCode', headerName: 'Disposition', width: 130, valueFormatter: labelValueFormatter},
    {field: 'recordStatusCode', headerName: 'Status', width: 100, valueFormatter: labelValueFormatter},
  ];

  protected accepts(record: InmateOffenseRecord): boolean {
    return (
      record.recordCategoryCode === "institutional_conduct" ||
      record.recordCategoryCode === "custody_event"
    );
  }
}
